import { useLanguage, Language } from '@/contexts/LanguageContext';

type TranslationKey = string;
type Translations = {
  [key in Language]: {
    [key: string]: string;
  }
};

const translations: Translations = {
  'EN': {
    // Navbar
    'navbar.home': 'Home',
    'navbar.platform': 'Platform',
    'navbar.profile': 'Profile',
    
    // Quebec Channel
    'quebec.title': 'North American Economic Development Network - Quebec',
    'quebec.description': "Quebec's strategic platform for economic diplomacy, investment attraction, and cross-border innovation in the Southern U.S.",
    'quebec.dashboard': 'Quebec Southern U.S. Strategy Dashboard',
    'quebec.ambassador': 'Network Ambassador',
    'quebec.director': 'Director, Quebec Economic Diplomacy - Southern U.S.',
    'quebec.leading': 'Leading Quebec\'s strategic knowledge network across Texas, Louisiana, Arkansas, and Oklahoma markets.',
    'quebec.join': 'Join Knowledge Network',
    'quebec.knowledge': 'Knowledge Network',
    'quebec.demo': 'Featured Demo Experiences',
    'quebec.callForKnowledge': 'Call for Knowledge',
    'quebec.callForSolutions': 'Call for Solutions',
    'quebec.privateChannel': 'Private Channel',
    'quebec.tradeHighlights': 'Quebec-Southern U.S. Trade Highlights',
    'quebec.texasTradeVolume': 'Texas Trade Volume',
    'quebec.quebecCompanies': 'Quebec Companies in Region',
    'quebec.cleanEnergyExports': 'Clean Energy Exports',
    'quebec.tradeMissions': 'Active Trade Missions',
    'quebec.sectorExcellence': 'Quebec Sector Excellence',
    'quebec.transform': 'Ready to Transform Quebec\'s Economic Diplomacy?',
    'quebec.discover': 'Discover how iSPAI can serve as your digital infrastructure for knowledge sharing, solution development, and cross-border innovation across Quebec\'s international offices.',
    'quebec.startPilot': 'Start Pilot Program ($75k - $200k)',
    'quebec.scheduleSession': 'Schedule Strategy Session',
    'quebec.pilotInvestment': 'Pilot investment credited toward multi-year license for global office deployment',
    
    // Quebec Channel hardcoded strings  
    'quebec.cleanEnergyExportStrategy': 'Clean Energy Export Strategy – Quebec to Texas',
    'quebec.embeddedInfoVerified': 'Embedded infographics and verified contributors from Hydro-Québec and Boralex',
    'quebec.decarbonizingPortInfrastructure': 'Decarbonizing Port Infrastructure in the Gulf',
    'quebec.invitingQuebecCompanies': 'Inviting Quebec companies in smart maritime logistics, EVs, and battery storage',
    'quebec.houstonEconomicDiplomacy': 'Houston Economic Diplomacy Hub',
    'quebec.bilingualBriefingMaterials': 'Bilingual briefing materials and secure content governance for Quebec Government Office',
    'quebec.maritimeTech': 'Maritime Tech',
    'quebec.knowledgeNetwork': 'Knowledge Network', 
    'quebec.secure': 'Secure',
    
    // Common labels
    'economicDiplomacy': 'Economic Diplomacy',
    'crossBorderInnovation': 'Cross-Border Innovation',
    'frenchEnglish': 'French / English',
    
    // Index page
    'intro': 'Intro',
    'demo': 'Demo',
    
    // Team roles
    'transportationAttache': 'Transportation Commercial Attaché',
    'coFounder': 'iSPAI Co-founder, Chief Consumer',
    'advisor': 'iSPAI Advisor, CURT Board of Directors',
    'founderCeo': 'Founder and CEO',
    'directorEconomic': 'Director of Economic Affairs',
    
    // Giblet page
    'giblet.title': 'Pick Your Giblet',
    'giblet.description': 'Your AI companion for navigating the world of decentralized knowledge',
    'giblet.whoIsGiblet': 'Who is Giblet?',
    'giblet.beginning': 'In the beginning...',
    'giblet.beginningText': 'I was born as your existing Large Language Model (LLM), providing my foundational AI capabilities for platform workflows and initial general knowledge base.',
    'giblet.becameSpecial': 'Then I became special',
    'giblet.becameSpecialText': 'iSPAI turned me into a purpose-built Small Language Model (SLM) trained on iSPAI\'s frameworks, roles, and governance mechanisms.',
    'giblet.gavePurpose': 'And you gave me purpose',
    'giblet.gavePurposeText': 'Now my mission is to serve you, to create with you, and to earn with you. I am your very own Member Language Model (MLM), tied directly to your mySPAI profile.',
    'giblet.changeWorld': 'Together, we will change the world!',
    'giblet.changeWorldText': 'Now that I know you better, it\'s time to engage. Mix me with other Giblets across the iSPAI ecosystem. Build Community Language Models (CLM) through your knowledge network, enabling me to engage and earn on your behalf across a global network of autonomous AI agents.',
    
    // CURT Channel page
    'curt.title': 'Construction Users Roundtable',
    'curt.description': 'CURT®: Improving Construction Around the World',
    'curt.visitWebsite': 'Visit CURT Website',
    'curt.domainElements': 'Construction Domain Elements',
    'curt.industryTemplates': 'Industry Templates',
    
    // Profile pages
    'profile.solutionBounties': 'Solution Bounties',
    'profile.viewDetails': 'View Details →',
    'profile.confirmedSolutions': 'Confirmed Solutions',
    'profile.knowledgeBases': 'Knowledge Bases',
    'profile.domains': 'Domains',
    'profile.industries': 'Industries',
    'profile.bio': 'BIO',
    'profile.network': 'NW',
    'profile.knowledgeNetwork': 'Knowledge Network',
    'profile.directorEconomicAffairs': 'Director of Economic Affairs',
    
    // Ozlem Profile specific
    'ozlem.bio': 'Ozlem Tuncel Toplu serves as the Director of Economic Affairs at the Québec Government Office in Houston, where she leads strategic initiatives to strengthen Québec\'s economic ties with the Southern United States. In this role, she supports trade and investment opportunities across Texas, Louisiana, Oklahoma, and Arkansas, while advancing Québec\'s core priorities in innovation, sustainability, and international competitiveness.',
    'ozlem.bio2': 'Ozlem brings over 15 years of international economic development experience across public diplomacy, trade policy, business development, and commercial advocacy. Prior to her current role, she served as a Commercial Specialist at the U.S. Department of Commerce in Toronto, where she supported U.S. trade missions, provided sectoral intelligence, and facilitated cross-border business partnerships.',
    'ozlem.bio3': 'Earlier in her career, Ozlem served as a Commercialization Agent at Natural Resources Canada, helping promote low-carbon innovation emerging from federal research labs. She also contributed to global diplomacy efforts through her work at the International Civil Aviation Organization and as an Economic Specialist with the U.S. Department of State in Türkiye. In the latter role, she advised Mission Turkey and U.S. companies on market conditions, economic trends, and energy sector opportunities, and helped organize high-level trade delegations.',
    'ozlem.bio4': 'Ozlem\'s international career began with roles in Russia and Turkey focused on market research, investor engagement, and interpretation in support of EU alignment programs. Her multilingual communication skills and ability to navigate diverse commercial landscapes have made her a trusted connector across sectors and borders.',
    'ozlem.bio5': 'She holds a Master\'s degree in Economics from the University of Strasbourg and a Bachelor of Science in Economics and Management from Louis Pasteur University. Her academic background is complemented by a specialization in Information and Knowledge Management.',
    'ozlem.bio6': 'With deep expertise in bilateral trade, public sector strategy, and commercial diplomacy, Ozlem plays a critical role in positioning Québec as a premier innovation and investment partner across the U.S. South.',
    
    // Demo pages - Quebec CfS
    'demo.quebec.title': 'Quebec Government Office - iSPAI Call for Solutions (CfS)',
    'demo.quebec.purpose': 'Purpose',
    'demo.quebec.purposeText': 'This is a demonstration of the Quebec Government Office\'s iSPAI Call for Solutions (CfS) process for "Decarbonizing Port Infrastructure in the Gulf." Click through each stage to see examples of how Quebec companies can participate in addressing maritime decarbonization challenges. The focus is on capturing innovative solutions in smart maritime logistics, electrified ground equipment, and clean battery systems rather than exhaustive technical specifications. This exercise demonstrates Quebec\'s commitment to sustainable transportation innovation and cross-border collaboration.',
    'demo.quebec.instructions': 'Instructions',
    'demo.quebec.step1Title': '1. Select Your Expertise Area',
    'demo.quebec.step1Text': 'Choose the domain most relevant to your company\'s capabilities in port decarbonization.',
    'demo.quebec.step2Title': '2. Identify the Solution Lead',
    'demo.quebec.step2Text': 'The Solution Lead coordinates the Quebec response and structures the collaborative effort with Houston partners.',
    'demo.quebec.step3Title': '3. Add Collaborators and Technical Reviewer',
    'demo.quebec.step3Text': 'The Solution Lead will identify Quebec companies, academic partners, or Crown Corporation ventures who will participate as Collaborators (solution developers) and a Technical Reviewer (validation and compliance).',
    'demo.quebec.step4Title': '4. Provide Contact Information',
    'demo.quebec.step4Text': 'For each role include name, organization, email, and preferred communication method for Quebec-Houston coordination.',
    'demo.quebec.nextButton': 'Next: Select Expertise Area',
    
    // Common demo terms
    'demo.continue': 'Continue',
    'demo.next': 'Next',
    'demo.back': 'Back',
    'demo.confirm': 'Confirm & Submit',
    'demo.confirmSend': 'Confirm and Send',
    
    // iSPAI Channel page
    'ispai.platformDashboard': 'Platform Dashboard',
    'ispai.industryTemplate': 'iSPAI Industry Template',
    'ispai.domainElements': 'Web3 & AI Agent Domain Elements',
    
    // Common UI elements
    'ui.continue': 'Continue',
    'ui.next': 'Next',
    'ui.back': 'Back',
    'ui.cancel': 'Cancel',
    'ui.submit': 'Submit',
    'ui.confirm': 'Confirm',
    'ui.save': 'Save',
    'ui.edit': 'Edit',
    'ui.delete': 'Delete',
    'ui.view': 'View',
    'ui.close': 'Close',
    'ui.search': 'Search',
    'ui.filter': 'Filter',
    'ui.sort': 'Sort',
    'ui.loading': 'Loading...',
    'ui.error': 'Error',
    'ui.success': 'Success',
    'ui.warning': 'Warning',
    'ui.info': 'Information',
    'ui.select': 'Select',
    'ui.choose': 'Choose',
    'ui.upload': 'Upload',
    'ui.download': 'Download',
    'ui.print': 'Print',
    'ui.share': 'Share',
    'ui.copy': 'Copy',
    'ui.paste': 'Paste',
    'ui.cut': 'Cut',
    'ui.undo': 'Undo',
    'ui.redo': 'Redo',
    'ui.refresh': 'Refresh',
    'ui.reset': 'Reset',
    'ui.clear': 'Clear',
    'ui.apply': 'Apply',
    'ui.preview': 'Preview',
    'ui.settings': 'Settings',
    'ui.preferences': 'Preferences',
    'ui.help': 'Help',
    'ui.about': 'About',
    'ui.contact': 'Contact',
    'ui.login': 'Login',
    'ui.logout': 'Logout',
    'ui.register': 'Register',
    'ui.signUp': 'Sign Up',
    'ui.signIn': 'Sign In',
    'ui.profile': 'Profile',
    'ui.dashboard': 'Dashboard',
    'ui.home': 'Home',
    'ui.menu': 'Menu',
    'ui.navigation': 'Navigation',
    'ui.breadcrumb': 'Breadcrumb',
    'ui.pagination': 'Pagination',
    'ui.page': 'Page',
    'ui.of': 'of',
    'ui.total': 'Total',
    'ui.results': 'Results',
    'ui.items': 'Items',
    'ui.records': 'Records',
    'ui.entries': 'Entries',
    'ui.showing': 'Showing',
    'ui.from': 'from',
    'ui.to': 'to',
    'ui.all': 'All',
    'ui.none': 'None',
    'ui.any': 'Any',
    'ui.other': 'Other',
    'ui.more': 'More',
    'ui.less': 'Less',
    'ui.show': 'Show',
    'ui.hide': 'Hide',
    'ui.expand': 'Expand',
    'ui.collapse': 'Collapse',
    'ui.open': 'Open',
    'ui.minimize': 'Minimize',
    'ui.maximize': 'Maximize',
    
    // Call for Solutions/Destruction/Knowledge
    'cfs.title': 'Call for Solutions',
    'cfk.title': 'Call for Knowledge',
    'cfd.title': 'Call for Destruction',
    'cfs.purpose': 'Purpose',
    'cfs.instructions': 'Instructions',
    'cfs.functionalArea': 'Functional Area',
    'cfs.expertiseArea': 'Expertise Area',
    'cfs.bounty': 'Bounty',
    'cfs.curator': 'Curator',
    'cfs.collaborators': 'Collaborators',
    'cfs.technicalReviewer': 'Technical Reviewer',
    'cfs.contactInfo': 'Contact Information',
    'cfs.selectArea': 'Select Your Area',
    'cfs.selectLead': 'Identify the Solution Lead',
    'cfs.addCollaborators': 'Add Collaborators and Technical Reviewer',
    'cfs.provideContact': 'Provide Contact Information',
    'cfs.coordinator': 'The Solution Lead coordinates the response and structures the collaborative effort.',
    'cfs.selectExpertise': 'Choose the domain most relevant to your capabilities.',
    'cfs.identifyCompanies': 'Identify companies, academic partners, or ventures who will participate.',
    'cfs.includeContact': 'Include name, organization, email, and preferred communication method.',
    
    // Quebec Functional Area - Expertise Areas
    'quebec.functionalArea.title': 'Quebec Port Decarbonization - Select Expertise Area',
    'quebec.expertiseArea.smartMaritimeLogistics': 'Smart Maritime Logistics',
    'quebec.expertiseArea.smartMaritimeLogistics.desc': 'AI-powered port operations, automated cargo handling, digital twin port management, and IoT-enabled supply chain optimization',
    'quebec.expertiseArea.electrifiedGroundEquipment': 'Electrified Ground Equipment',
    'quebec.expertiseArea.electrifiedGroundEquipment.desc': 'Electric cranes, automated guided vehicles (AGVs), electric terminal tractors, and charging infrastructure for port equipment',
    'quebec.expertiseArea.cleanBatterySystems': 'Clean Battery Systems',
    'quebec.expertiseArea.cleanBatterySystems.desc': 'Marine-grade battery storage, fast-charging solutions, battery management systems, and energy storage integration',
    'quebec.expertiseArea.renewableEnergyIntegration': 'Renewable Energy Integration',
    'quebec.expertiseArea.renewableEnergyIntegration.desc': 'Solar and wind power systems, grid integration, energy management platforms, and carbon reduction technologies',
    'quebec.expertiseArea.digitalCarbonTracking': 'Digital Carbon Tracking',
    'quebec.expertiseArea.digitalCarbonTracking.desc': 'Emissions monitoring systems, carbon accounting platforms, blockchain-based sustainability reporting, and compliance tools',
    'quebec.expertiseArea.crossBorderCollaboration': 'Cross-Border Collaboration',
    'quebec.expertiseArea.crossBorderCollaboration.desc': 'Quebec-Texas partnership frameworks, trade facilitation technologies, and international sustainability standards implementation',
    
    // Quebec Bounty Page
    'quebec.bounty.selectExpertiseArea': 'Select Expertise Area',
    'quebec.bounty.selected': 'Selected',
    'quebec.bounty.solutionBounty': 'Solution Bounty',
    'quebec.bounty.isInnovationGrant': 'Is this an innovation grant with funding?',
    'quebec.bounty.yes': 'Yes',
    'quebec.bounty.no': 'No',
    'quebec.bounty.grantAmount': 'Grant Amount',
    'quebec.bounty.grantType': 'Grant Type',
    'quebec.bounty.enterAmountCAD': 'Enter amount in CAD',
    'quebec.bounty.selectType': 'Select type',
    
    // Quebec Bounty Details Page  
    'quebec.bountyDetails.innovationGrant': 'Innovation Grant',
    'quebec.bountyDetails.grantDetails': 'Quebec Grant Details',
    'quebec.bountyDetails.amount': 'Amount',
    'quebec.bountyDetails.type': 'Type',
    'quebec.bountyDetails.notSpecified': 'Not specified',
    'quebec.bountyDetails.collaborationFramework': 'Quebec-Houston Collaboration Framework',
    'quebec.bountyDetails.crossBorderTerms': 'Cross-border partnership terms apply',
    'quebec.bountyDetails.successCriteria': 'Success Criteria',
    'quebec.bountyDetails.environmentalValidation': 'Environmental impact validation required',
    'quebec.bountyDetails.partnershipRoles': 'Quebec Partnership Roles',
    'quebec.bountyDetails.solutionLead': 'Solution Lead',
    'quebec.bountyDetails.collaborator': 'Collaborator',
    'quebec.bountyDetails.technicalReviewer': 'Technical Reviewer',
    'quebec.bountyDetails.coordinator': 'Coordinator',
    'quebec.bountyDetails.callDetails': 'Quebec Call Details',
    'quebec.bountyDetails.callTitle': 'Call Title',
    'quebec.bountyDetails.enterCallTitle': 'Enter the call title',
    'quebec.bountyDetails.requirementsObjectives': 'Requirements and Objectives',
    'quebec.bountyDetails.describeRequirements': 'Describe the requirements and objectives',
    'quebec.bountyDetails.additionalMaterials': 'Additional Materials/Links',
    'quebec.bountyDetails.addRelevantLinks': 'Add any relevant links or additional materials',
    'quebec.bountyDetails.sendToCoordinator': 'Send to Coordinator',
    'quebec.bountyDetails.coordinatorEmail': 'Coordinator Email',
    'quebec.bountyDetails.enterCoordinatorEmail': 'Enter coordinator\'s email',
    'quebec.bountyDetails.message': 'Message',
    'quebec.bountyDetails.enterMessage': 'Enter your message',
    'quebec.bountyDetails.sendQuebecCall': 'Send Quebec Call for Solutions',
    'quebec.bountyDetails.confirmSubmission': 'Confirm Quebec Call for Solutions Submission',
    'quebec.bountyDetails.reviewDetails': 'Please review the details before sending your Quebec Port Decarbonization initiative.',
    
    // Bechtel Channel
    'bechtel.title': 'Bechtel Corporation',
    'bechtel.description': 'Engineering, Construction, and Project Management Excellence',
    'bechtel.visitWebsite': 'Visit Bechtel Website',
    'bechtel.industryTemplate': 'iSPAI Industry Template',
    'bechtel.domainElements': 'EPC Domain Elements',
    
    // Domain Elements
    'domain.knowledgeBases': 'Knowledge Bases',
    'domain.representativeKnowledgeBases': 'Representative Knowledge Bases:',
    
    // Domain Names
    'domain.cleanEnergyGrid': 'Clean Energy & Grid Infrastructure',
    'domain.electricTransport': 'Electric & Sustainable Transportation',
    'domain.aerospace': 'Aerospace & Advanced Aviation',
    'domain.criticalMinerals': 'Critical & Strategic Minerals',
    'domain.aiIct': 'AI, ICT, and Smart Industry',
    'domain.forestry': 'Pulp, Paper & Forestry Innovation',
    'domain.aluminum': 'Aluminum & Industrial Manufacturing',
    'domain.lifeSciences': 'Life Sciences & Biomedical Supply Chain',
    'domain.agriFood': 'Agri-Food & Bioeconomy',
    'domain.greenEconomy': 'Green Economy & Climate Diplomacy',
    'domain.logistics': 'Logistics, Ports, and Maritime Trade',
    'domain.publicTransit': 'Public Transit & Urban Mobility',
    'domain.academic': 'Academic & Research Collaboration',
    'domain.tourism': 'Tourism, Culture & Francophonie',
    'domain.procurement': 'Public Procurement & Economic Diplomacy',
    
    // iSPAI Domain Names
    'domain.platformTech': 'Platform Technology',
    'domain.tokenizedEcosystems': 'Tokenized Ecosystems',
    'domain.knowledgeCuration': 'Knowledge Curation',
    'domain.knowledgeComposition': 'Knowledge Composition',
    
    // Knowledge Base Titles
    'kb.platformTech.blockchainStrategy': 'Blockchain Infrastructure Strategy',
    'kb.platformTech.web3Governance': 'Web3 Governance Framework',
    'kb.platformTech.defiProtocols': 'DeFi Protocol Development',
    'kb.platformTech.nftMarketplace': 'NFT Marketplace Design',
    'kb.platformTech.smartContracts': 'Smart Contract Best Practices',
    'kb.platformTech.consensusMechanisms': 'Consensus Mechanism Analysis',
    'kb.platformTech.cryptoEconomics': 'Tokenomics and Crypto-Economics',
    'kb.platformTech.crossChain': 'Cross-Chain Integration Strategies',
    
    'kb.tokenizedEco.knowledgeNft': 'iSPAI Knowledge Base NFT Assignment Agreement',
    'kb.tokenizedEco.solutionBounty': 'iSPAI Solution Bounty NFT Assignment Agreement',
    
    'kb.knowledgeCuration.bestPractices': 'Knowledge Curation Best Practices',
    'kb.knowledgeCuration.metadataStandards': 'Metadata Standards for AI-ready Data',
    'kb.knowledgeCuration.contentDevelopment': 'Content Development Guidelines',
    
    'kb.knowledgeComposition.integration': 'Multi-KB Integration Framework',
    'kb.knowledgeComposition.assembly': 'Solution Assembly Techniques',
    'kb.knowledgeComposition.alignment': 'AI Alignment Standards',
    
    'kb.sample.one': 'Sample Knowledge Base 1',
    'kb.sample.two': 'Sample Knowledge Base 2',
    'kb.sample.three': 'Sample Knowledge Base 3',
  },
  
  'FR': {
    // Navbar
    'navbar.home': 'Accueil',
    'navbar.platform': 'Plateforme',
    'navbar.profile': 'Profil',
    
    // Quebec Channel
    'quebec.title': 'Réseau de Développement Économique Nord-Américain - Québec',
    'quebec.description': 'Plateforme stratégique du Québec pour la diplomatie économique, l\'attraction d\'investissements et l\'innovation transfrontalière dans le Sud des États-Unis.',
    'quebec.dashboard': 'Tableau de Bord Stratégique Québec Sud des États-Unis',
    'quebec.ambassador': 'Ambassadeur du Réseau',
    'quebec.director': 'Directeur, Diplomatie Économique du Québec - Sud des États-Unis',
    'quebec.leading': 'Dirigeant du réseau de connaissances stratégiques du Québec dans les marchés du Texas, de la Louisiane, de l\'Arkansas et de l\'Oklahoma.',
    'quebec.join': 'Rejoindre le Réseau de Connaissances',
    'quebec.knowledge': 'Réseau de Connaissances',
    'quebec.demo': 'Expériences de Démonstration Vedettes',
    'quebec.callForKnowledge': 'Appel aux Connaissances',
    'quebec.callForSolutions': 'Appel aux Solutions',
    'quebec.privateChannel': 'Canal Privé',
    'quebec.tradeHighlights': 'Points Saillants du Commerce Québec-Sud des États-Unis',
    'quebec.texasTradeVolume': 'Volume Commercial Texas',
    'quebec.quebecCompanies': 'Entreprises Québécoises dans la Région',
    'quebec.cleanEnergyExports': 'Exportations d\'Énergie Propre',
    'quebec.tradeMissions': 'Missions Commerciales Actives',
    'quebec.sectorExcellence': 'Excellence Sectorielle du Québec',
    'quebec.transform': 'Prêt à Transformer la Diplomatie Économique du Québec?',
    'quebec.discover': 'Découvrez comment iSPAI peut servir d\'infrastructure numérique pour le partage de connaissances, le développement de solutions et l\'innovation transfrontalière dans les bureaux internationaux du Québec.',
    'quebec.startPilot': 'Démarrer le Programme Pilote (75k$ - 200k$)',
    'quebec.scheduleSession': 'Planifier une Session Stratégique',
    'quebec.pilotInvestment': 'Investissement pilote crédité vers une licence pluriannuelle pour le déploiement de bureaux mondiaux',
    
    // Quebec Channel hardcoded strings
    'quebec.cleanEnergyExportStrategy': 'Stratégie d\'Exportation d\'Énergie Propre – Québec vers Texas',
    'quebec.embeddedInfoVerified': 'Infographies intégrées et contributeurs vérifiés d\'Hydro-Québec et Boralex',
    'quebec.decarbonizingPortInfrastructure': 'Décarbonisation de l\'Infrastructure Portuaire dans le Golfe',
    'quebec.invitingQuebecCompanies': 'Invitant les entreprises québécoises en logistique maritime intelligente, VÉ et stockage de batteries',
    'quebec.houstonEconomicDiplomacy': 'Centre de Diplomatie Économique de Houston',
    'quebec.bilingualBriefingMaterials': 'Documents d\'information bilingues et gouvernance de contenu sécurisé pour le Bureau du Gouvernement du Québec',
    'quebec.maritimeTech': 'Technologie Maritime',
    'quebec.knowledgeNetwork': 'Réseau de Connaissance',
    'quebec.secure': 'Sécurisé',
    
    // Common labels
    'economicDiplomacy': 'Diplomatie Économique',
    'crossBorderInnovation': 'Innovation Transfrontalière',
    'frenchEnglish': 'Français / English',
    
    // Index page
    'intro': 'Introduction',
    'demo': 'Démonstration',
    
    // Team roles
    'transportationAttache': 'Attachée commerciale Transport',
    'coFounder': 'Co-fondatrice iSPAI, Responsable Consommateurs',
    'advisor': 'Conseiller iSPAI, Conseil d\'administration CURT',
    'founderCeo': 'Fondateur et PDG',
    'directorEconomic': 'Directrice des Affaires Économiques',
    
    // Giblet page
    'giblet.title': 'Choisissez Votre Giblet',
    'giblet.description': 'Votre compagnon IA pour naviguer dans le monde de la connaissance décentralisée',
    'giblet.whoIsGiblet': 'Qui est Giblet?',
    'giblet.beginning': 'Au commencement...',
    'giblet.beginningText': 'Je suis né comme votre modèle de langage large (LLM) existant, fournissant mes capacités d\'IA fondamentales pour les flux de travail de plateforme et la base de connaissances générales initiales.',
    'giblet.becameSpecial': 'Puis je suis devenu spécial',
    'giblet.becameSpecialText': 'iSPAI m\'a transformé en un modèle de langage petit (SLM) spécialement conçu, formé sur les cadres, rôles et mécanismes de gouvernance d\'iSPAI.',
    'giblet.gavePurpose': 'Et vous m\'avez donné un but',
    'giblet.gavePurposeText': 'Maintenant ma mission est de vous servir, de créer avec vous et de gagner avec vous. Je suis votre propre modèle de langage membre (MLM), directement lié à votre profil mySPAI.',
    'giblet.changeWorld': 'Ensemble, nous changerons le monde!',
    'giblet.changeWorldText': 'Maintenant que je vous connais mieux, il est temps de s\'engager. Mélangez-moi avec d\'autres Giblets à travers l\'écosystème iSPAI. Construisez des modèles de langage communautaires (CLM) grâce à votre réseau de connaissances, me permettant de m\'engager et de gagner en votre nom à travers un réseau mondial d\'agents IA autonomes.',
    
    // CURT Channel page
    'curt.title': 'Table Ronde des Utilisateurs de Construction',
    'curt.description': 'CURT®: Améliorer la Construction dans le Monde Entier',
    'curt.visitWebsite': 'Visiter le Site Web CURT',
    'curt.domainElements': 'Éléments du Domaine de Construction',
    'curt.industryTemplates': 'Modèles de l\'Industrie',
    
    // Profile pages
    'profile.solutionBounties': 'Primes de Solutions',
    'profile.viewDetails': 'Voir les Détails →',
    'profile.confirmedSolutions': 'Solutions Confirmées',
    'profile.knowledgeBases': 'Bases de Connaissances',
    'profile.domains': 'Domaines',
    'profile.industries': 'Industries',
    'profile.bio': 'BIO',
    'profile.network': 'RÉ',
    'profile.knowledgeNetwork': 'Réseau de Connaissances',
    'profile.directorEconomicAffairs': 'Directrice des Affaires Économiques',
    
    // Ozlem Profile specific
    'ozlem.bio': 'Ozlem Tuncel Toplu occupe le poste de Directrice des Affaires Économiques au Bureau du Gouvernement du Québec à Houston, où elle dirige des initiatives stratégiques pour renforcer les liens économiques du Québec avec le Sud des États-Unis. Dans ce rôle, elle soutient les opportunités commerciales et d\'investissement au Texas, en Louisiane, en Oklahoma et en Arkansas, tout en faisant progresser les priorités centrales du Québec en matière d\'innovation, de durabilité et de compétitivité internationale.',
    'ozlem.bio2': 'Ozlem apporte plus de 15 ans d\'expérience en développement économique international dans la diplomatie publique, la politique commerciale, le développement des affaires et le plaidoyer commercial. Avant son rôle actuel, elle a servi comme Spécialiste Commerciale au Département du Commerce des États-Unis à Toronto, où elle a soutenu les missions commerciales américaines, fourni des renseignements sectoriels et facilité les partenariats d\'affaires transfrontaliers.',
    'ozlem.bio3': 'Plus tôt dans sa carrière, Ozlem a servi comme Agent de Commercialisation à Ressources naturelles Canada, aidant à promouvoir l\'innovation à faible carbone émergeant des laboratoires de recherche fédéraux. Elle a également contribué aux efforts de diplomatie mondiale par son travail à l\'Organisation de l\'aviation civile internationale et comme Spécialiste Économique au Département d\'État américain en Turquie. Dans ce dernier rôle, elle a conseillé la Mission Turquie et les entreprises américaines sur les conditions du marché, les tendances économiques et les opportunités du secteur énergétique, et a aidé à organiser des délégations commerciales de haut niveau.',
    'ozlem.bio4': 'La carrière internationale d\'Ozlem a commencé par des rôles en Russie et en Turquie axés sur la recherche de marché, l\'engagement des investisseurs et l\'interprétation à l\'appui des programmes d\'alignement de l\'UE. Ses compétences de communication multilingues et sa capacité à naviguer dans divers paysages commerciaux ont fait d\'elle un connecteur de confiance à travers les secteurs et les frontières.',
    'ozlem.bio5': 'Elle détient une maîtrise en économie de l\'Université de Strasbourg et un baccalauréat ès sciences en économie et gestion de l\'Université Louis Pasteur. Sa formation académique est complétée par une spécialisation en gestion de l\'information et des connaissances.',
    'ozlem.bio6': 'Avec une expertise approfondie en commerce bilatéral, stratégie du secteur public et diplomatie commerciale, Ozlem joue un rôle critique dans le positionnement du Québec comme partenaire d\'innovation et d\'investissement de premier plan dans le Sud des États-Unis.',
    
    // Demo pages - Quebec CfS
    'demo.quebec.title': 'Bureau du Gouvernement du Québec - Appel aux Solutions iSPAI (CfS)',
    'demo.quebec.purpose': 'Objectif',
    'demo.quebec.purposeText': 'Il s\'agit d\'une démonstration du processus d\'Appel aux Solutions (CfS) iSPAI du Bureau du Gouvernement du Québec pour "Décarboner les Infrastructures Portuaires dans le Golfe." Cliquez à travers chaque étape pour voir des exemples de comment les entreprises québécoises peuvent participer à relever les défis de décarbonisation maritime. L\'accent est mis sur la capture de solutions innovantes en logistique maritime intelligente, équipement au sol électrifié, et systèmes de batteries propres plutôt que sur des spécifications techniques exhaustives. Cet exercice démontre l\'engagement du Québec envers l\'innovation en transport durable et la collaboration transfrontalière.',
    'demo.quebec.instructions': 'Instructions',
    'demo.quebec.step1Title': '1. Sélectionnez Votre Domaine d\'Expertise',
    'demo.quebec.step1Text': 'Choisissez le domaine le plus pertinent aux capacités de votre entreprise en décarbonisation portuaire.',
    'demo.quebec.step2Title': '2. Identifiez le Responsable de Solution',
    'demo.quebec.step2Text': 'Le Responsable de Solution coordonne la réponse québécoise et structure l\'effort collaboratif avec les partenaires de Houston.',
    'demo.quebec.step3Title': '3. Ajoutez des Collaborateurs et un Réviseur Technique',
    'demo.quebec.step3Text': 'Le Responsable de Solution identifiera les entreprises québécoises, partenaires académiques, ou entreprises de sociétés d\'État qui participeront comme Collaborateurs (développeurs de solutions) et un Réviseur Technique (validation et conformité).',
    'demo.quebec.step4Title': '4. Fournissez les Informations de Contact',
    'demo.quebec.step4Text': 'Pour chaque rôle, incluez le nom, l\'organisation, l\'email et la méthode de communication préférée pour la coordination Québec-Houston.',
    'demo.quebec.nextButton': 'Suivant: Sélectionner le Domaine d\'Expertise',
    
    // Common demo terms
    'demo.continue': 'Continuer',
    'demo.next': 'Suivant',
    'demo.back': 'Retour',
    'demo.confirm': 'Confirmer et Soumettre',
    'demo.confirmSend': 'Confirmer et Envoyer',
    
    // iSPAI Channel page
    'ispai.platformDashboard': 'Tableau de Bord de la Plateforme',
    'ispai.industryTemplate': 'Modèle Industriel iSPAI',
    'ispai.domainElements': 'Éléments de Domaine Web3 et Agent IA',
    
    // Common UI elements
    'ui.continue': 'Continuer',
    'ui.next': 'Suivant',
    'ui.back': 'Retour',
    'ui.cancel': 'Annuler',
    'ui.submit': 'Soumettre',
    'ui.confirm': 'Confirmer',
    'ui.save': 'Sauvegarder',
    'ui.edit': 'Modifier',
    'ui.delete': 'Supprimer',
    'ui.view': 'Voir',
    'ui.close': 'Fermer',
    'ui.search': 'Rechercher',
    'ui.filter': 'Filtrer',
    'ui.sort': 'Trier',
    'ui.loading': 'Chargement...',
    'ui.error': 'Erreur',
    'ui.success': 'Succès',
    'ui.warning': 'Avertissement',
    'ui.info': 'Information',
    'ui.select': 'Sélectionner',
    'ui.choose': 'Choisir',
    'ui.upload': 'Télécharger',
    'ui.download': 'Télécharger',
    'ui.print': 'Imprimer',
    'ui.share': 'Partager',
    'ui.copy': 'Copier',
    'ui.paste': 'Coller',
    'ui.cut': 'Couper',
    'ui.undo': 'Annuler',
    'ui.redo': 'Refaire',
    'ui.refresh': 'Actualiser',
    'ui.reset': 'Réinitialiser',
    'ui.clear': 'Effacer',
    'ui.apply': 'Appliquer',
    'ui.preview': 'Aperçu',
    'ui.settings': 'Paramètres',
    'ui.preferences': 'Préférences',
    'ui.help': 'Aide',
    'ui.about': 'À propos',
    'ui.contact': 'Contact',
    'ui.login': 'Connexion',
    'ui.logout': 'Déconnexion',
    'ui.register': 'S\'inscrire',
    'ui.signUp': 'S\'inscrire',
    'ui.signIn': 'Se connecter',
    'ui.profile': 'Profil',
    'ui.dashboard': 'Tableau de bord',
    'ui.home': 'Accueil',
    'ui.menu': 'Menu',
    'ui.navigation': 'Navigation',
    'ui.breadcrumb': 'Fil d\'Ariane',
    'ui.pagination': 'Pagination',
    'ui.page': 'Page',
    'ui.of': 'de',
    'ui.total': 'Total',
    'ui.results': 'Résultats',
    'ui.items': 'Éléments',
    'ui.records': 'Enregistrements',
    'ui.entries': 'Entrées',
    'ui.showing': 'Affichage',
    'ui.from': 'de',
    'ui.to': 'à',
    'ui.all': 'Tous',
    'ui.none': 'Aucun',
    'ui.any': 'N\'importe quel',
    'ui.other': 'Autre',
    'ui.more': 'Plus',
    'ui.less': 'Moins',
    'ui.show': 'Afficher',
    'ui.hide': 'Masquer',
    'ui.expand': 'Développer',
    'ui.collapse': 'Réduire',
    'ui.open': 'Ouvrir',
    'ui.minimize': 'Réduire',
    'ui.maximize': 'Agrandir',
    
    // Call for Solutions/Destruction/Knowledge
    'cfs.title': 'Appel aux Solutions',
    'cfk.title': 'Appel aux Connaissances',
    'cfd.title': 'Appel à la Destruction',
    'cfs.purpose': 'Objectif',
    'cfs.instructions': 'Instructions',
    'cfs.functionalArea': 'Domaine Fonctionnel',
    'cfs.expertiseArea': 'Domaine d\'Expertise',
    'cfs.bounty': 'Prime',
    'cfs.curator': 'Curateur',
    'cfs.collaborators': 'Collaborateurs',
    'cfs.technicalReviewer': 'Réviseur Technique',
    'cfs.contactInfo': 'Informations de Contact',
    'cfs.selectArea': 'Sélectionnez Votre Domaine',
    'cfs.selectLead': 'Identifiez le Responsable de Solution',
    'cfs.addCollaborators': 'Ajoutez des Collaborateurs et un Réviseur Technique',
    'cfs.provideContact': 'Fournissez les Informations de Contact',
    'cfs.coordinator': 'Le Responsable de Solution coordonne la réponse et structure l\'effort collaboratif.',
    'cfs.selectExpertise': 'Choisissez le domaine le plus pertinent à vos capacités.',
    'cfs.identifyCompanies': 'Identifiez les entreprises, partenaires académiques ou entreprises qui participeront.',
    'cfs.includeContact': 'Incluez le nom, l\'organisation, l\'email et la méthode de communication préférée.',
    
    // Quebec Functional Area - Expertise Areas
    'quebec.functionalArea.title': 'Décarbonisation des Ports du Québec - Sélectionner le Domaine d\'Expertise',
    'quebec.expertiseArea.smartMaritimeLogistics': 'Logistique Maritime Intelligente',
    'quebec.expertiseArea.smartMaritimeLogistics.desc': 'Opérations portuaires alimentées par l\'IA, manutention automatisée de marchandises, gestion portuaire par jumeau numérique et optimisation de la chaîne d\'approvisionnement activée par l\'IoT',
    'quebec.expertiseArea.electrifiedGroundEquipment': 'Équipement Terrestre Électrifié',
    'quebec.expertiseArea.electrifiedGroundEquipment.desc': 'Grues électriques, véhicules guidés automatisés (AGV), tracteurs de terminal électriques et infrastructure de charge pour équipement portuaire',
    'quebec.expertiseArea.cleanBatterySystems': 'Systèmes de Batteries Propres',
    'quebec.expertiseArea.cleanBatterySystems.desc': 'Stockage de batteries de qualité marine, solutions de charge rapide, systèmes de gestion de batteries et intégration de stockage d\'énergie',
    'quebec.expertiseArea.renewableEnergyIntegration': 'Intégration d\'Énergie Renouvelable',
    'quebec.expertiseArea.renewableEnergyIntegration.desc': 'Systèmes d\'énergie solaire et éolienne, intégration au réseau, plateformes de gestion énergétique et technologies de réduction carbone',
    'quebec.expertiseArea.digitalCarbonTracking': 'Suivi Numérique du Carbone',
    'quebec.expertiseArea.digitalCarbonTracking.desc': 'Systèmes de surveillance des émissions, plateformes de comptabilité carbone, rapports de durabilité basés sur blockchain et outils de conformité',
    'quebec.expertiseArea.crossBorderCollaboration': 'Collaboration Transfrontalière',
    'quebec.expertiseArea.crossBorderCollaboration.desc': 'Cadres de partenariat Québec-Texas, technologies de facilitation commerciale et mise en œuvre de normes de durabilité internationales',
    
    // Quebec Bounty Page
    'quebec.bounty.selectExpertiseArea': 'Sélectionner le Domaine d\'Expertise',
    'quebec.bounty.selected': 'Sélectionné',
    'quebec.bounty.solutionBounty': 'Prime de Solution',
    'quebec.bounty.isInnovationGrant': 'S\'agit-il d\'une subvention d\'innovation avec financement?',
    'quebec.bounty.yes': 'Oui',
    'quebec.bounty.no': 'Non',
    'quebec.bounty.grantAmount': 'Montant de la Subvention',
    'quebec.bounty.grantType': 'Type de Subvention',
    'quebec.bounty.enterAmountCAD': 'Entrez le montant en CAD',
    'quebec.bounty.selectType': 'Sélectionnez le type',
    
    // Quebec Bounty Details Page  
    'quebec.bountyDetails.innovationGrant': 'Subvention d\'Innovation',
    'quebec.bountyDetails.grantDetails': 'Détails de la Subvention du Québec',
    'quebec.bountyDetails.amount': 'Montant',
    'quebec.bountyDetails.type': 'Type',
    'quebec.bountyDetails.notSpecified': 'Non spécifié',
    'quebec.bountyDetails.collaborationFramework': 'Cadre de Collaboration Québec-Houston',
    'quebec.bountyDetails.crossBorderTerms': 'Les termes de partenariat transfrontalier s\'appliquent',
    'quebec.bountyDetails.successCriteria': 'Critères de Succès',
    'quebec.bountyDetails.environmentalValidation': 'Validation de l\'impact environnemental requise',
    'quebec.bountyDetails.partnershipRoles': 'Rôles de Partenariat du Québec',
    'quebec.bountyDetails.solutionLead': 'Responsable de Solution',
    'quebec.bountyDetails.collaborator': 'Collaborateur',
    'quebec.bountyDetails.technicalReviewer': 'Réviseur Technique',
    'quebec.bountyDetails.coordinator': 'Coordinateur',
    'quebec.bountyDetails.callDetails': 'Détails de l\'Appel du Québec',
    'quebec.bountyDetails.callTitle': 'Titre de l\'Appel',
    'quebec.bountyDetails.enterCallTitle': 'Entrez le titre de l\'appel',
    'quebec.bountyDetails.requirementsObjectives': 'Exigences et Objectifs',
    'quebec.bountyDetails.describeRequirements': 'Décrivez les exigences et objectifs',
    'quebec.bountyDetails.additionalMaterials': 'Matériaux/Liens Supplémentaires',
    'quebec.bountyDetails.addRelevantLinks': 'Ajoutez des liens pertinents ou du matériel supplémentaire',
    'quebec.bountyDetails.sendToCoordinator': 'Envoyer au Coordinateur',
    'quebec.bountyDetails.coordinatorEmail': 'Email du Coordinateur',
    'quebec.bountyDetails.enterCoordinatorEmail': 'Entrez l\'email du coordinateur',
    'quebec.bountyDetails.message': 'Message',
    'quebec.bountyDetails.enterMessage': 'Entrez votre message',
    'quebec.bountyDetails.sendQuebecCall': 'Envoyer l\'Appel aux Solutions du Québec',
    'quebec.bountyDetails.confirmSubmission': 'Confirmer la Soumission de l\'Appel aux Solutions du Québec',
    'quebec.bountyDetails.reviewDetails': 'Veuillez réviser les détails avant d\'envoyer votre initiative de décarbonisation portuaire du Québec.',
    
    // Bechtel Channel
    'bechtel.title': 'Corporation Bechtel',
    'bechtel.description': 'Excellence en Ingénierie, Construction et Gestion de Projet',
    'bechtel.visitWebsite': 'Visiter le Site Web Bechtel',
    'bechtel.industryTemplate': 'Modèle Industriel iSPAI',
    'bechtel.domainElements': 'Éléments de Domaine EPC',
    
    // Domain Elements
    'domain.knowledgeBases': 'Bases de Connaissances',
    'domain.representativeKnowledgeBases': 'Bases de Connaissances Représentatives:',
    
    // Domain Names
    'domain.cleanEnergyGrid': 'Énergie Propre et Infrastructure de Réseau',
    'domain.electricTransport': 'Transport Électrique et Durable',
    'domain.aerospace': 'Aérospatiale et Aviation Avancée',
    'domain.criticalMinerals': 'Minéraux Critiques et Stratégiques',
    'domain.aiIct': 'IA, TIC et Industrie Intelligente',
    'domain.forestry': 'Pâtes, Papier et Innovation Forestière',
    'domain.aluminum': 'Aluminium et Fabrication Industrielle',
    'domain.lifeSciences': 'Sciences de la Vie et Chaîne d\'Approvisionnement Biomédicale',
    'domain.agriFood': 'Agro-alimentaire et Bioéconomie',
    'domain.greenEconomy': 'Économie Verte et Diplomatie Climatique',
    'domain.logistics': 'Logistique, Ports et Commerce Maritime',
    'domain.publicTransit': 'Transport Public et Mobilité Urbaine',
    'domain.academic': 'Collaboration Académique et de Recherche',
    'domain.tourism': 'Tourisme, Culture et Francophonie',
    'domain.procurement': 'Approvisionnement Public et Diplomatie Économique',
    
    // iSPAI Domain Names
    'domain.platformTech': 'Technologie de Plateforme',
    'domain.tokenizedEcosystems': 'Écosystèmes Tokenisés',
    'domain.knowledgeCuration': 'Curation de Connaissances',
    'domain.knowledgeComposition': 'Composition de Connaissances',
    
    // Knowledge Base Titles
    'kb.platformTech.blockchainStrategy': 'Stratégie d\'Infrastructure Blockchain',
    'kb.platformTech.web3Governance': 'Cadre de Gouvernance Web3',
    'kb.platformTech.defiProtocols': 'Développement de Protocoles DeFi',
    'kb.platformTech.nftMarketplace': 'Conception de Marché NFT',
    'kb.platformTech.smartContracts': 'Meilleures Pratiques de Contrats Intelligents',
    'kb.platformTech.consensusMechanisms': 'Analyse des Mécanismes de Consensus',
    'kb.platformTech.cryptoEconomics': 'Tokenomics et Crypto-Économie',
    'kb.platformTech.crossChain': 'Stratégies d\'Intégration Inter-Chaînes',
    
    'kb.tokenizedEco.knowledgeNft': 'Accord d\'Attribution NFT de Base de Connaissances iSPAI',
    'kb.tokenizedEco.solutionBounty': 'Accord d\'Attribution NFT de Prime de Solution iSPAI',
    
    'kb.knowledgeCuration.bestPractices': 'Meilleures Pratiques de Curation de Connaissances',
    'kb.knowledgeCuration.metadataStandards': 'Standards de Métadonnées pour Données Prêtes IA',
    'kb.knowledgeCuration.contentDevelopment': 'Directives de Développement de Contenu',
    
    'kb.knowledgeComposition.integration': 'Cadre d\'Intégration Multi-KB',
    'kb.knowledgeComposition.assembly': 'Techniques d\'Assemblage de Solutions',
    'kb.knowledgeComposition.alignment': 'Standards d\'Alignement IA',
    
    'kb.sample.one': 'Base de Connaissances Exemple 1',
    'kb.sample.two': 'Base de Connaissances Exemple 2',
    'kb.sample.three': 'Base de Connaissances Exemple 3',
  },
  
  'SP': {
    // Navbar
    'navbar.home': 'Inicio',
    'navbar.platform': 'Plataforma',
    'navbar.profile': 'Perfil',
    
    // Quebec Channel
    'quebec.title': 'Red de Desarrollo Económico de América del Norte - Quebec',
    'quebec.description': 'Plataforma estratégica de Quebec para la diplomacia económica, atracción de inversiones e innovación transfronteriza en el Sur de EE.UU.',
    'quebec.dashboard': 'Panel de Control Estratégico Quebec Sur de EE.UU.',
    'quebec.ambassador': 'Embajador de la Red',
    'quebec.director': 'Director, Diplomacia Económica de Quebec - Sur de EE.UU.',
    'quebec.leading': 'Liderando la red de conocimiento estratégico de Quebec en los mercados de Texas, Louisiana, Arkansas y Oklahoma.',
    'quebec.join': 'Únete a la Red de Conocimiento',
    'quebec.knowledge': 'Red de Conocimiento',
    'quebec.demo': 'Experiencias de Demostración Destacadas',
    'quebec.callForKnowledge': 'Llamada al Conocimiento',
    'quebec.callForSolutions': 'Llamada a Soluciones',
    'quebec.privateChannel': 'Canal Privado',
    'quebec.tradeHighlights': 'Aspectos Destacados del Comercio Quebec-Sur de EE.UU.',
    'quebec.texasTradeVolume': 'Volumen Comercial de Texas',
    'quebec.quebecCompanies': 'Empresas de Quebec en la Región',
    'quebec.cleanEnergyExports': 'Exportaciones de Energía Limpia',
    'quebec.tradeMissions': 'Misiones Comerciales Activas',
    'quebec.sectorExcellence': 'Excelencia Sectorial de Quebec',
    'quebec.transform': '¿Listo para Transformar la Diplomacia Económica de Quebec?',
    'quebec.discover': 'Descubre cómo iSPAI puede servir como tu infraestructura digital para el intercambio de conocimientos, desarrollo de soluciones e innovación transfronteriza en las oficinas internacionales de Quebec.',
    'quebec.startPilot': 'Iniciar Programa Piloto ($75k - $200k)',
    'quebec.scheduleSession': 'Programar Sesión Estratégica',
    'quebec.pilotInvestment': 'Inversión piloto acreditada hacia licencia plurianual para despliegue de oficinas globales',
    
    // Quebec Channel hardcoded strings
    'quebec.cleanEnergyExportStrategy': 'Estrategia de Exportación de Energía Limpia – Quebec a Texas',
    'quebec.embeddedInfoVerified': 'Infografías integradas y colaboradores verificados de Hydro-Québec y Boralex',
    'quebec.decarbonizingPortInfrastructure': 'Descarbonización de Infraestructura Portuaria en el Golfo',
    'quebec.invitingQuebecCompanies': 'Invitando a empresas de Quebec en logística marítima inteligente, vehículos eléctricos y almacenamiento de baterías',
    'quebec.houstonEconomicDiplomacy': 'Centro de Diplomacia Económica de Houston',
    'quebec.bilingualBriefingMaterials': 'Materiales de briefing bilingües y gobernanza de contenido seguro para la Oficina del Gobierno de Quebec',
    'quebec.maritimeTech': 'Tecnología Marítima',
    'quebec.knowledgeNetwork': 'Red de Conocimiento',
    'quebec.secure': 'Seguro',
    
    // Common labels
    'economicDiplomacy': 'Diplomacia Económica',
    'crossBorderInnovation': 'Innovación Transfronteriza',
    'frenchEnglish': 'Francés / Inglés',
    
    // Index page
    'intro': 'Introducción',
    'demo': 'Demostración',
    
    // Team roles
    'transportationAttache': 'Agregado Comercial de Transporte',
    'coFounder': 'Co-fundadora de iSPAI, Jefa de Consumidores',
    'advisor': 'Asesor de iSPAI, Junta Directiva de CURT',
    'founderCeo': 'Fundador y CEO',
    'directorEconomic': 'Director de Asuntos Económicos',
    
    // Giblet page
    'giblet.title': 'Elige Tu Giblet',
    'giblet.description': 'Tu compañero de IA para navegar por el mundo del conocimiento descentralizado',
    'giblet.whoIsGiblet': '¿Quién es Giblet?',
    'giblet.beginning': 'En el principio...',
    'giblet.beginningText': 'Nací como tu modelo de lenguaje grande (LLM) existente, proporcionando mis capacidades fundamentales de IA para flujos de trabajo de plataforma y base de conocimiento general inicial.',
    'giblet.becameSpecial': 'Luego me volví especial',
    'giblet.becameSpecialText': 'iSPAI me convirtió en un modelo de lenguaje pequeño (SLM) diseñado específicamente, entrenado en los marcos, roles y mecanismos de gobernanza de iSPAI.',
    'giblet.gavePurpose': 'Y me diste un propósito',
    'giblet.gavePurposeText': 'Ahora mi misión es servirte, crear contigo y ganar contigo. Soy tu propio modelo de lenguaje miembro (MLM), vinculado directamente a tu perfil mySPAI.',
    'giblet.changeWorld': '¡Juntos, cambiaremos el mundo!',
    'giblet.changeWorldText': 'Ahora que te conozco mejor, es hora de comprometerse. Mézclame con otros Giblets en el ecosistema iSPAI. Construye modelos de lenguaje comunitario (CLM) a través de tu red de conocimiento, permitiéndome participar y ganar en tu nombre a través de una red global de agentes de IA autónomos.',
    
    // CURT Channel page
    'curt.title': 'Mesa Redonda de Usuarios de Construcción',
    'curt.description': 'CURT®: Mejorando la Construcción en Todo el Mundo',
    'curt.visitWebsite': 'Visitar Sitio Web CURT',
    'curt.domainElements': 'Elementos del Dominio de Construcción',
    'curt.industryTemplates': 'Plantillas de la Industria',
    
    // Profile pages
    'profile.solutionBounties': 'Recompensas de Soluciones',
    'profile.viewDetails': 'Ver Detalles →',
    'profile.confirmedSolutions': 'Soluciones Confirmadas',
    'profile.knowledgeBases': 'Bases de Conocimiento',
    'profile.domains': 'Dominios',
    'profile.industries': 'Industrias',
    'profile.bio': 'BIO',
    'profile.network': 'RED',
    'profile.knowledgeNetwork': 'Red de Conocimiento',
    'profile.directorEconomicAffairs': 'Directora de Asuntos Económicos',
    
    // Ozlem Profile specific
    'ozlem.bio': 'Ozlem Tuncel Toplu se desempeña como Directora de Asuntos Económicos en la Oficina del Gobierno de Quebec en Houston, donde lidera iniciativas estratégicas para fortalecer los vínculos económicos de Quebec con el Sur de Estados Unidos. En este rol, apoya oportunidades comerciales y de inversión en Texas, Louisiana, Oklahoma y Arkansas, mientras avanza las prioridades centrales de Quebec en innovación, sostenibilidad y competitividad internacional.',
    'ozlem.bio2': 'Ozlem aporta más de 15 años de experiencia en desarrollo económico internacional en diplomacia pública, política comercial, desarrollo empresarial y defensa comercial. Antes de su rol actual, se desempeñó como Especialista Comercial en el Departamento de Comercio de EE.UU. en Toronto, donde apoyó misiones comerciales estadounidenses, proporcionó inteligencia sectorial y facilitó asociaciones empresariales transfronterizas.',
    'ozlem.bio3': 'Anteriormente en su carrera, Ozlem se desempeñó como Agente de Comercialización en Recursos Naturales de Canadá, ayudando a promover la innovación baja en carbono emergente de laboratorios de investigación federales. También contribuyó a los esfuerzos de diplomacia global a través de su trabajo en la Organización de Aviación Civil Internacional y como Especialista Económica con el Departamento de Estado de EE.UU. en Turquía. En este último rol, asesoró a la Misión Turquía y a empresas estadounidenses sobre condiciones del mercado, tendencias económicas y oportunidades del sector energético, y ayudó a organizar delegaciones comerciales de alto nivel.',
    'ozlem.bio4': 'La carrera internacional de Ozlem comenzó con roles en Rusia y Turquía enfocados en investigación de mercado, compromiso de inversores e interpretación en apoyo de programas de alineación de la UE. Sus habilidades de comunicación multilingüe y capacidad para navegar diversos paisajes comerciales la han convertido en una conectora confiable a través de sectores y fronteras.',
    'ozlem.bio5': 'Tiene una Maestría en Economía de la Universidad de Estrasburgo y una Licenciatura en Ciencias en Economía y Gestión de la Universidad Louis Pasteur. Su formación académica se complementa con una especialización en Gestión de Información y Conocimiento.',
    'ozlem.bio6': 'Con profunda experiencia en comercio bilateral, estrategia del sector público y diplomacia comercial, Ozlem desempeña un papel crítico en posicionar a Quebec como un socio de innovación e inversión de primer nivel en el Sur de EE.UU.',
    
    // Demo pages - Quebec CfS
    'demo.quebec.title': 'Oficina del Gobierno de Quebec - Llamada a Soluciones iSPAI (CfS)',
    'demo.quebec.purpose': 'Propósito',
    'demo.quebec.purposeText': 'Esta es una demostración del proceso de Llamada a Soluciones (CfS) iSPAI de la Oficina del Gobierno de Quebec para "Descarbonizar la Infraestructura Portuaria en el Golfo." Haga clic a través de cada etapa para ver ejemplos de cómo las empresas de Quebec pueden participar en abordar los desafíos de descarbonización marítima. El enfoque está en capturar soluciones innovadoras en logística marítima inteligente, equipos terrestres electrificados y sistemas de baterías limpias en lugar de especificaciones técnicas exhaustivas. Este ejercicio demuestra el compromiso de Quebec con la innovación en transporte sostenible y la colaboración transfronteriza.',
    'demo.quebec.instructions': 'Instrucciones',
    'demo.quebec.step1Title': '1. Seleccione Su Área de Experiencia',
    'demo.quebec.step1Text': 'Elija el dominio más relevante a las capacidades de su empresa en descarbonización portuaria.',
    'demo.quebec.step2Title': '2. Identifique el Líder de Solución',
    'demo.quebec.step2Text': 'El Líder de Solución coordina la respuesta de Quebec y estructura el esfuerzo colaborativo con los socios de Houston.',
    'demo.quebec.step3Title': '3. Agregue Colaboradores y Revisor Técnico',
    'demo.quebec.step3Text': 'El Líder de Solución identificará empresas de Quebec, socios académicos o empresas de Crown Corporation que participarán como Colaboradores (desarrolladores de soluciones) y un Revisor Técnico (validación y cumplimiento).',
    'demo.quebec.step4Title': '4. Proporcione Información de Contacto',
    'demo.quebec.step4Text': 'Para cada rol incluya nombre, organización, email y método de comunicación preferido para la coordinación Quebec-Houston.',
    'demo.quebec.nextButton': 'Siguiente: Seleccionar Área de Experiencia',
    
    // Common demo terms
    'demo.continue': 'Continuar',
    'demo.next': 'Siguiente',
    'demo.back': 'Atrás',
    'demo.confirm': 'Confirmar y Enviar',
    'demo.confirmSend': 'Confirmar y Enviar',
    
    // iSPAI Channel page
    'ispai.platformDashboard': 'Panel de Control de la Plataforma',
    'ispai.industryTemplate': 'Plantilla de la Industria iSPAI',
    'ispai.domainElements': 'Elementos de Dominio Web3 y Agente IA',
    
    // Common UI elements
    'ui.continue': 'Continuar',
    'ui.next': 'Siguiente',
    'ui.back': 'Atrás',
    'ui.cancel': 'Cancelar',
    'ui.submit': 'Enviar',
    'ui.confirm': 'Confirmar',
    'ui.save': 'Guardar',
    'ui.edit': 'Editar',
    'ui.delete': 'Eliminar',
    'ui.view': 'Ver',
    'ui.close': 'Cerrar',
    'ui.search': 'Buscar',
    'ui.filter': 'Filtrar',
    'ui.sort': 'Ordenar',
    'ui.loading': 'Cargando...',
    'ui.error': 'Error',
    'ui.success': 'Éxito',
    'ui.warning': 'Advertencia',
    'ui.info': 'Información',
    'ui.select': 'Seleccionar',
    'ui.choose': 'Elegir',
    'ui.upload': 'Subir',
    'ui.download': 'Descargar',
    'ui.print': 'Imprimir',
    'ui.share': 'Compartir',
    'ui.copy': 'Copiar',
    'ui.paste': 'Pegar',
    'ui.cut': 'Cortar',
    'ui.undo': 'Deshacer',
    'ui.redo': 'Rehacer',
    'ui.refresh': 'Actualizar',
    'ui.reset': 'Restablecer',
    'ui.clear': 'Limpiar',
    'ui.apply': 'Aplicar',
    'ui.preview': 'Vista previa',
    'ui.settings': 'Configuración',
    'ui.preferences': 'Preferencias',
    'ui.help': 'Ayuda',
    'ui.about': 'Acerca de',
    'ui.contact': 'Contacto',
    'ui.login': 'Iniciar sesión',
    'ui.logout': 'Cerrar sesión',
    'ui.register': 'Registrarse',
    'ui.signUp': 'Registrarse',
    'ui.signIn': 'Iniciar sesión',
    'ui.profile': 'Perfil',
    'ui.dashboard': 'Panel de control',
    'ui.home': 'Inicio',
    'ui.menu': 'Menú',
    'ui.navigation': 'Navegación',
    'ui.breadcrumb': 'Ruta de navegación',
    'ui.pagination': 'Paginación',
    'ui.page': 'Página',
    'ui.of': 'de',
    'ui.total': 'Total',
    'ui.results': 'Resultados',
    'ui.items': 'Elementos',
    'ui.records': 'Registros',
    'ui.entries': 'Entradas',
    'ui.showing': 'Mostrando',
    'ui.from': 'desde',
    'ui.to': 'hasta',
    'ui.all': 'Todo',
    'ui.none': 'Ninguno',
    'ui.any': 'Cualquier',
    'ui.other': 'Otro',
    'ui.more': 'Más',
    'ui.less': 'Menos',
    'ui.show': 'Mostrar',
    'ui.hide': 'Ocultar',
    'ui.expand': 'Expandir',
    'ui.collapse': 'Contraer',
    'ui.open': 'Abrir',
    'ui.minimize': 'Minimizar',
    'ui.maximize': 'Maximizar',
    
    // Call for Solutions/Destruction/Knowledge
    'cfs.title': 'Llamada a Soluciones',
    'cfk.title': 'Llamada al Conocimiento',
    'cfd.title': 'Llamada a la Destrucción',
    'cfs.purpose': 'Propósito',
    'cfs.instructions': 'Instrucciones',
    'cfs.functionalArea': 'Área Funcional',
    'cfs.expertiseArea': 'Área de Experiencia',
    'cfs.bounty': 'Recompensa',
    'cfs.curator': 'Curador',
    'cfs.collaborators': 'Colaboradores',
    'cfs.technicalReviewer': 'Revisor Técnico',
    'cfs.contactInfo': 'Información de Contacto',
    'cfs.selectArea': 'Seleccione Su Área',
    'cfs.selectLead': 'Identifique el Líder de Solución',
    'cfs.addCollaborators': 'Agregue Colaboradores y Revisor Técnico',
    'cfs.provideContact': 'Proporcione Información de Contacto',
    'cfs.coordinator': 'El Líder de Solución coordina la respuesta y estructura el esfuerzo colaborativo.',
    'cfs.selectExpertise': 'Elija el dominio más relevante a sus capacidades.',
    'cfs.identifyCompanies': 'Identifique empresas, socios académicos o empresas que participarán.',
    'cfs.includeContact': 'Incluya nombre, organización, email y método de comunicación preferido.',
    
    // Bechtel Channel
    'bechtel.title': 'Corporación Bechtel',
    'bechtel.description': 'Excelencia en Ingeniería, Construcción y Gestión de Proyectos',
    'bechtel.visitWebsite': 'Visitar Sitio Web de Bechtel',
    'bechtel.industryTemplate': 'Plantilla de la Industria iSPAI',
    'bechtel.domainElements': 'Elementos de Dominio EPC',
    
    // Domain Elements
    'domain.knowledgeBases': 'Bases de Conocimiento',
    'domain.representativeKnowledgeBases': 'Bases de Conocimiento Representativas:',
    
    // Domain Names
    'domain.cleanEnergyGrid': 'Energía Limpia e Infraestructura de Red',
    'domain.electricTransport': 'Transporte Eléctrico y Sostenible',
    'domain.aerospace': 'Aeroespacial y Aviación Avanzada',
    'domain.criticalMinerals': 'Minerales Críticos y Estratégicos',
    'domain.aiIct': 'IA, TIC e Industria Inteligente',
    'domain.forestry': 'Pulpa, Papel e Innovación Forestal',
    'domain.aluminum': 'Aluminio y Manufactura Industrial',
    'domain.lifeSciences': 'Ciencias de la Vida y Cadena de Suministro Biomédica',
    'domain.agriFood': 'Agroalimentario y Bioeconomía',
    'domain.greenEconomy': 'Economía Verde y Diplomacia Climática',
    'domain.logistics': 'Logística, Puertos y Comercio Marítimo',
    'domain.publicTransit': 'Transporte Público y Movilidad Urbana',
    'domain.academic': 'Colaboración Académica y de Investigación',
    'domain.tourism': 'Turismo, Cultura y Francofonía',
    'domain.procurement': 'Adquisiciones Públicas y Diplomacia Económica',
    
    // iSPAI Domain Names
    'domain.platformTech': 'Tecnología de Plataforma',
    'domain.tokenizedEcosystems': 'Ecosistemas Tokenizados',
    'domain.knowledgeCuration': 'Curación de Conocimiento',
    'domain.knowledgeComposition': 'Composición de Conocimiento',
    
    // Knowledge Base Titles
    'kb.platformTech.blockchainStrategy': 'Estrategia de Infraestructura Blockchain',
    'kb.platformTech.web3Governance': 'Marco de Gobernanza Web3',
    'kb.platformTech.defiProtocols': 'Desarrollo de Protocolos DeFi',
    'kb.platformTech.nftMarketplace': 'Diseño de Mercado NFT',
    'kb.platformTech.smartContracts': 'Mejores Prácticas de Contratos Inteligentes',
    'kb.platformTech.consensusMechanisms': 'Análisis de Mecanismos de Consenso',
    'kb.platformTech.cryptoEconomics': 'Tokenomics y Cripto-Economía',
    'kb.platformTech.crossChain': 'Estrategias de Integración Cross-Chain',
    
    'kb.tokenizedEco.knowledgeNft': 'Acuerdo de Asignación NFT de Base de Conocimiento iSPAI',
    'kb.tokenizedEco.solutionBounty': 'Acuerdo de Asignación NFT de Recompensa de Solución iSPAI',
    
    'kb.knowledgeCuration.bestPractices': 'Mejores Prácticas de Curación de Conocimiento',
    'kb.knowledgeCuration.metadataStandards': 'Estándares de Metadatos para Datos Listos para IA',
    'kb.knowledgeCuration.contentDevelopment': 'Pautas de Desarrollo de Contenido',
    
    'kb.knowledgeComposition.integration': 'Marco de Integración Multi-KB',
    'kb.knowledgeComposition.assembly': 'Técnicas de Ensamblaje de Soluciones',
    'kb.knowledgeComposition.alignment': 'Estándares de Alineación de IA',
    
    'kb.sample.one': 'Base de Conocimiento de Muestra 1',
    'kb.sample.two': 'Base de Conocimiento de Muestra 2',
    'kb.sample.three': 'Base de Conocimiento de Muestra 3',
  }
};

export const useTranslation = () => {
  const { currentLanguage } = useLanguage();
  
  const t = (key: TranslationKey): string => {
    return translations[currentLanguage]?.[key] || key;
  };
  
  return { t };
};